import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBackgroundMusicContext } from '../../context/BackgroundMusicContext';
import { useIntroOutroContext } from '../../context/IntroOutroContext';
import { useTextPanelContext } from '../../context/TextPanelContext';
import { TrimmingList } from '../../routes/newVideo/VideoSidebarMenu/TrimmingList/TrimmingList';
import BackgroundMusicEditor from '../BackgroundMusicEditor/BackgroundMusicEditor';
import { CustomButton } from '../Button/Button';
import SpoilerButton from '../SpoilerButton/SpoilerButton';
import RenameEditor from '../RenameEditor/RenameEditor';
import TextPanelEditor from '../TextPanelEditor/TextPanelEditor';
import TransitionChooser from '../TransitionChooser/TransitionChooser';
import styles from './EditorSidebarActions.module.scss';
import cx from 'classnames';
import { useVideoSpeedUpContext } from '../../context/VideoSpeedUpContext';
import VideoSpeedUpEditor from '../VideoSpeedUpEditor/VideoSpeedUpEditor';
import IntroOutroEditor from '../IntroOutroEditor/IntroOutroEditor';
import LogoChooser from '../../routes/newVideo/VideoSidebarMenu/LogoChooser/LogoChooser';
import { useLogoContext } from '../../context/LogoContext';
import { useUserContext } from '../../context/UserContext';
import SidebarButton from '../../routes/newVideo/VideoSidebarMenu/SidebarButton/SidebarButton';
import { useZoomContext } from '../../context/ZoomContext';
import { ZoomVideoEditor } from '../../features/ZoomVideo';
import useWindowSize from '../../hooks/useWindowSize';
import { CustomButtonTooltip } from '../ButtonTooltip/ButtonTooltip';

const EditorSidebarActions = ({
  video,
  logo,
  isDraft,
  currentVideoPart,
  sceneIndex,
  activeScene,
  rename,
  handleRename,
  handleSaveClip,
  saving,
  isSceneProcessing,
  cancelEditing,
  handleUndo,
  handleRedo,
  controlsDisabled,
  trimmingMode,
  handleTrimVideo,
  init,
  hasChanged,
  handleStartTrimming,
  switchingTrimming,
  isTransitionExists,
  isTransitionAllowed,
  activeTransitionRightMedia,
  onTransitionChanged,
  currentTransitionType,
  handleSaveTextPanel,
  handleIntroOutro,
  onBackgroundMusicChange,
  handleSpeedUpChange,
  handleSplit,
  userPermissions,
  handleLogoChange,
  handleZoomPointsChange,
  mini,
  setMiniSidebar,
  addTransitionToScene,
  isRecording,
  handleEditStoryboard,
  handleCompleteAssignment,
  isCurrentAssignedScenesCompleted,
  isCurrentAssignedScenesRecorded,
  onEnhanceScene,
  onRemoveEnhanceScene,
}) => {
  const { isTextPanelEditor, enableTextPanelEditor } = useTextPanelContext();
  const {
    isIntroOutroOpened,
    toggleIntroOutroOpened,
    isIntroOutroLoading,
    setBlockPlayingIntroOutro,
    introOutroConfig,
  } = useIntroOutroContext();

  const { isBackgroundMusicOpened, enableBackgroundMusic } = useBackgroundMusicContext();
  const { isVideoSpeedUpOpened, toggleVideoSpeedUpOpened } = useVideoSpeedUpContext();
  const { isLogoEditor, enableLogoEditor } = useLogoContext();
  const { isZoomOpened, enableZoom } = useZoomContext();
  const { logFirebaseEvent, isZoomFeatureEnabled, isSceneSpeedEnabled } = useUserContext();

  const [isScrollable, setIsScrollable] = useState(false);

  const { width, height } = useWindowSize();

  const { appConfig } = useUserContext();

  const scrollableContentRef = useRef(null);

  const isLogoEditable = useMemo(() => video?.simulatedResources?.includes('logo') || video?.simulatedResources?.includes('*'), [
    video,
  ]);

  const isIntroOutroAvailable = useMemo(() => {
    if (introOutroConfig.length === 0) {
      return false;
    } else {
      let reply = false;
      let hasIntroOutro = introOutroConfig.some(item => item?.intro !== null || item.outro !== null);

      if (hasIntroOutro) {
        reply = !saving && !init && !isIntroOutroLoading && userPermissions?.updateStory;
      }
      return reply;
    }
  }, [saving, init, isIntroOutroLoading, userPermissions, introOutroConfig]);

  const isLogoButtonDisabled = useMemo(
    () => saving || init || activeScene?.processing || !isLogoEditable || !userPermissions?.updateStory,
    [saving, init, activeScene, isDraft, isLogoEditable, userPermissions]
  );

  const enableTrimming = useMemo(
    () => userPermissions?.updateStory || userPermissions?.allowedScenes.find(item => item === activeScene?.id),
    [activeScene, userPermissions]
  );

  const updatePermission = useMemo(() => userPermissions.updateStory, [userPermissions]);

  const editorOptionShown = useMemo(() => {
    return (
      trimmingMode ||
      switchingTrimming ||
      isTextPanelEditor ||
      isBackgroundMusicOpened ||
      rename.flag ||
      isIntroOutroOpened ||
      isVideoSpeedUpOpened ||
      isLogoEditor ||
      isZoomOpened
    );
  }, [
    trimmingMode,
    switchingTrimming,
    isTextPanelEditor,
    isBackgroundMusicOpened,
    rename,
    isIntroOutroOpened,
    isVideoSpeedUpOpened,
    isLogoEditor,
    isZoomOpened,
  ]);

  const closeFeatures = useMemo(() => (rename.flag ? true : false), [rename]);

  const { t } = useTranslation();

  const handleTransition = useCallback(() => {
    addTransitionToScene(Number(currentVideoPart), false);
    setMiniSidebar(false);
  }, [sceneIndex, addTransitionToScene]);

  useEffect(() => {
    setBlockPlayingIntroOutro(isBackgroundMusicOpened || isVideoSpeedUpOpened || isTextPanelEditor || isLogoEditor || isZoomOpened);
  }, [isBackgroundMusicOpened, isVideoSpeedUpOpened, isTextPanelEditor, isLogoEditor, isZoomOpened]);

  useEffect(() => {
    if (scrollableContentRef?.current) {
      const result = scrollableContentRef?.current?.scrollHeight > scrollableContentRef?.current?.clientHeight;
      setIsScrollable(result);
    }
  }, [mini, width, height]);

  return (
    <div className={cx(styles.container, { [styles.miniContainer]: mini, [styles.miniContainerScrollable]: isScrollable })}>
      {trimmingMode || switchingTrimming ? (
        <TrimmingList
          activeScene={activeScene}
          trimVideo={handleTrimVideo}
          splitVideo={handleSplit}
          confirmOnClose={false}
          onClose={handleTrimVideo}
          videoId={video.id}
          saveButtonLabel={t('general.done')}
          switchingTrimming={switchingTrimming}
          enableSplit={updatePermission}
          miniSidebar={mini}
          closeFeatures={closeFeatures}
        />
      ) : null}
      {isTextPanelEditor && (
        <TextPanelEditor updateVideoTexts={handleSaveTextPanel} miniSidebar={mini} closeFeatures={closeFeatures} />
      )}
      {isBackgroundMusicOpened && (
        <BackgroundMusicEditor
          activeScene={activeScene}
          currentVideoPart={currentVideoPart}
          onBackgroundMusicChange={onBackgroundMusicChange}
          miniSidebar={mini}
          closeFeatures={closeFeatures}
          onEnhanceScene={onEnhanceScene}
          onRemoveEnhanceScene={onRemoveEnhanceScene}
        />
      )}
      {isVideoSpeedUpOpened && (
        <VideoSpeedUpEditor
          activeScene={activeScene}
          currentVideoPart={currentVideoPart}
          handleSpeedUpChange={handleSpeedUpChange}
          miniSidebar={mini}
          closeFeatures={closeFeatures}
        />
      )}
      {rename.flag && (
        <RenameEditor rename={rename} handleRename={handleRename} miniSidebar={mini} cancelEditing={cancelEditing} saving={saving} />
      )}
      {isIntroOutroOpened && <IntroOutroEditor handleIntroOutro={handleIntroOutro} miniSidebar={mini} closeFeatures={closeFeatures} />}
      {isLogoEditor && (
        <LogoChooser
          videoLogo={logo}
          handleLogoChange={handleLogoChange}
          saveOnClose
          miniSidebar={mini}
          closeFeatures={closeFeatures}
        />
      )}
      {isZoomFeatureEnabled && isZoomOpened && (
        <ZoomVideoEditor
          activeScene={activeScene}
          currentVideoPart={currentVideoPart}
          videoDuration={activeScene?.trimmedDuration / (activeScene?.playbackRate || 1)}
          handleZoomPointsChange={handleZoomPointsChange}
          miniSidebar={mini}
          closeFeatures={closeFeatures}
        />
      )}
      {!editorOptionShown && (
        <>
          <div ref={scrollableContentRef} className={cx(styles.scrollableContent, { [styles.scrollable]: isScrollable })}>
            <div className={styles.staticContainer}>
              <div className={styles.controlsContainer}>
                <SidebarButton
                  label={t('my_video.tools.trim.undo')}
                  disabled={controlsDisabled.undo || saving || init}
                  icon="undo"
                  mini={mini}
                  onClick={handleUndo}
                  hoverText={controlsDisabled.undo && t('my_video.tools.trim.undo.no_action_tooltip')}
                  hoverOnDisabled
                  parentRef={scrollableContentRef}
                  classNameButton={styles.controlsBtn}
                  classNameWrapper={mini ? styles.btnWrapper : ''}
                  classNameTooltip={styles.tooltip}
                />
                <SidebarButton
                  label={t('my_video.tools.trim.redo')}
                  disabled={controlsDisabled.redo || saving || init}
                  icon="redo"
                  mini={mini}
                  onClick={handleRedo}
                  hoverText={controlsDisabled.redo && t('my_video.tools.trim.redo.no_action_tooltip')}
                  hoverOnDisabled
                  parentRef={scrollableContentRef}
                  classNameButton={styles.controlsBtn}
                  classNameWrapper={mini ? styles.btnWrapper : ''}
                  classNameTooltip={styles.tooltip}
                />
              </div>
            </div>
            <div
              className={cx(styles.moveContainer, {
                [styles.priorMoveContainer]: !activeTransitionRightMedia,
                [styles.hiddenOption]: activeTransitionRightMedia,
              })}
            >
              <div className={styles.optionContainer}>
                <SidebarButton
                  label={t('my_video.tools.trim')}
                  disabled={
                    saving ||
                    init ||
                    currentVideoPart === 'intro' ||
                    currentVideoPart === 'outro' ||
                    activeScene?.processing ||
                    !activeScene?.video ||
                    (isDraft && activeScene?.trimmedDuration === 0) ||
                    !enableTrimming
                  }
                  icon="trim"
                  mini={mini}
                  onClick={() => {
                    handleStartTrimming();
                    logFirebaseEvent('co_trim_cl', { story_id: video.id });
                    setMiniSidebar(false);
                  }}
                  hoverText={
                    activeScene?.processing
                      ? t('my_video.tools.processing_tooltip')
                      : !activeScene?.video && t('my_video.tools.empty_video_tooltip')
                  }
                  hoverOnDisabled
                  classNameButton={styles.controlsBtn}
                  classNameWrapper={mini ? styles.btnWrapper : ''}
                  classNameTooltip={styles.tooltip}
                />
              </div>
              {updatePermission && (
                <div className={styles.optionContainer}>
                  <SidebarButton
                    label={t('my_video.tools.add_text_panel')}
                    disabled={
                      saving ||
                      init ||
                      currentVideoPart === 'intro' ||
                      currentVideoPart === 'outro' ||
                      activeScene?.processing ||
                      !activeScene?.video ||
                      (isDraft && activeScene?.trimmedDuration === 0)
                    }
                    icon="textPanel"
                    mini={mini}
                    onClick={() => {
                      enableTextPanelEditor(activeScene?.id);
                      setMiniSidebar(false);
                    }}
                    hoverText={
                      activeScene?.processing
                        ? t('my_video.tools.processing_tooltip')
                        : !activeScene?.video && t('my_video.tools.empty_video_tooltip')
                    }
                    hoverOnDisabled
                    classNameButton={styles.controlsBtn}
                    classNameWrapper={mini ? styles.btnWrapper : ''}
                    classNameTooltip={styles.tooltip}
                  />
                </div>
              )}
              {isSceneSpeedEnabled && updatePermission && (
                <div className={styles.optionContainer}>
                  <SidebarButton
                    label={t('my_video.tools.speed.button')}
                    disabled={
                      saving ||
                      init ||
                      currentVideoPart === 'intro' ||
                      currentVideoPart === 'outro' ||
                      activeScene?.processing ||
                      !activeScene?.video ||
                      (isDraft && activeScene?.trimmedDuration === 0)
                    }
                    icon="sceneSpeed"
                    mini={mini}
                    onClick={() => {
                      toggleVideoSpeedUpOpened();
                      setMiniSidebar(false);
                    }}
                    hoverText={
                      activeScene?.processing
                        ? t('my_video.tools.processing_tooltip')
                        : !activeScene?.video
                          ? t('my_video.tools.empty_video_tooltip')
                          : ''
                    }
                    hoverOnDisabled
                    classNameButton={styles.controlsBtn}
                    classNameWrapper={mini ? styles.btnWrapper : ''}
                    classNameTooltip={styles.tooltip}
                  />
                </div>
              )}
              {updatePermission && (
                <div className={styles.optionContainer}>
                  <SidebarButton
                    label={t('my_video.tools.audio')}
                    disabled={
                      saving ||
                      init ||
                      activeScene?.processing ||
                      !activeScene?.video ||
                      currentVideoPart === 'intro' ||
                      currentVideoPart === 'outro'
                    }
                    icon="audio"
                    mini={mini}
                    onClick={() => {
                      enableBackgroundMusic();
                      setMiniSidebar(false);
                    }}
                    hoverText={
                      activeScene?.processing
                        ? t('my_video.tools.processing_tooltip')
                        : !activeScene?.video && t('my_video.tools.empty_video_tooltip')
                    }
                    hoverOnDisabled
                    classNameButton={styles.controlsBtn}
                    classNameWrapper={mini ? styles.btnWrapper : ''}
                    classNameTooltip={styles.tooltip}
                  />
                </div>
              )}
              {updatePermission && (
                <div className={styles.optionContainer}>
                  <SidebarButton
                    label={t('clip_organizer.intro_outro_button')}
                    disabled={!isIntroOutroAvailable}
                    icon="introOutro"
                    mini={mini}
                    onClick={() => {
                      toggleIntroOutroOpened();
                      setMiniSidebar(false);
                    }}
                    classNameButton={styles.controlsBtn}
                    classNameWrapper={mini ? styles.btnWrapper : ''}
                    classNameTooltip={styles.tooltip}
                  />
                </div>
              )}
              {(updatePermission && appConfig?.company_logos?.length) ? (
                <div className={styles.optionContainer}>
                  <SidebarButton
                    label={t('clip_organizer.logo_button')}
                    disabled={isLogoButtonDisabled}
                    icon="logoSidebar"
                    mini={mini}
                    onClick={() => {
                      enableLogoEditor();
                      setMiniSidebar(false);
                    }}
                    hoverText={
                      activeScene?.processing
                        ? t('my_video.tools.processing_tooltip')
                        : currentVideoPart !== 'intro' && currentVideoPart !== 'outro' && isLogoButtonDisabled && !isLogoEditable
                          ? t('logo_chooser.mobile_notification')
                          : ''
                    }
                    hoverOnDisabled
                    parentRef={scrollableContentRef}
                    classNameButton={styles.controlsBtn}
                    classNameWrapper={mini ? styles.btnWrapper : ''}
                    classNameTooltip={styles.tooltip}
                  />
                </div>
              ) : null}
              {updatePermission && (
                <div className={styles.optionContainer}>
                  <SidebarButton
                    variant="link"
                    onClick={handleTransition}
                    icon={'transition'}
                    label={t('general.video_action_button.transition')}
                    className={cx(styles.controlsBtn, { [styles.btnMini]: mini })}
                    disabled={
                      saving ||
                      init ||
                      activeScene?.processing ||
                      currentVideoPart === 'intro' ||
                      currentVideoPart === 'outro' ||
                      !isTransitionAllowed ||
                      isTransitionExists
                    }
                    hoverText={
                      (activeScene?.processing && t('my_video.tools.processing_tooltip')) ||
                      (!isTransitionAllowed && t('general.video_action_button.transition.last_clip_tooltip')) ||
                      (isTransitionExists && t('general.video_action_button.transition.tooltip'))
                    }
                    hoverOnDisabled
                    mini={mini}
                    parentRef={scrollableContentRef}
                    classNameButton={styles.controlsBtn}
                    classNameWrapper={mini ? styles.btnWrapper : ''}
                    classNameTooltip={styles.tooltip}
                  />
                </div>
              )}
              {isZoomFeatureEnabled && updatePermission && (
                <div className={styles.optionContainer}>
                  <SidebarButton
                    label={t('my_video.tools.zoom.title')}
                    parentRef={scrollableContentRef}
                    disabled={
                      saving ||
                      init ||
                      (isDraft && activeScene?.trimmedDuration === 0) ||
                      activeScene?.processing ||
                      currentVideoPart === 'intro' ||
                      currentVideoPart === 'outro'
                    }
                    icon="zoom"
                    mini={mini}
                    onClick={() => {
                      enableZoom();
                      setMiniSidebar(false);
                    }}
                    hoverText={
                      activeScene?.processing
                        ? t('my_video.tools.processing_tooltip')
                        : !activeScene?.video && t('my_video.tools.empty_video_tooltip')
                    }
                    hoverOnDisabled
                    classNameButton={styles.controlsBtn}
                    classNameWrapper={mini ? styles.btnWrapper : ''}
                    classNameTooltip={styles.tooltip}
                  />
                </div>
              )}
            </div>
            {updatePermission && (
              <div
                className={cx(styles.moveContainer, {
                  [styles.secondMoveContainer]: activeTransitionRightMedia,
                  [styles.hiddenOption]: !activeTransitionRightMedia,
                })}
              >
                <SpoilerButton
                  hideOption={!activeTransitionRightMedia}
                  label={t('general.video_action_button.transition')}
                  icon="transition"
                  disabled={saving || init || !activeTransitionRightMedia || !userPermissions?.updateStory}
                  index={1}
                  mini={mini}
                  onClick={() => {
                    setMiniSidebar(false);
                  }}
                >
                  <TransitionChooser active={currentTransitionType} onClicked={onTransitionChanged} mini={mini} />
                </SpoilerButton>
              </div>
            )}
          </div>
          {!mini && (
            <div className={styles.footer}>
              {!userPermissions.isOwner && !isCurrentAssignedScenesCompleted && (
                <CustomButtonTooltip
                  onClick={handleCompleteAssignment}
                  variant="basic"
                  label={t('clip_organizer.assignments.done')}
                  disabled={!isCurrentAssignedScenesRecorded}
                  tooltip={t('clip_organizer.assignments.done_tooltip')}
                  tooltipClassName={styles.topTooltip}
                  moveableTooltip={false}
                  className={styles.sidebarTooltipButton}
                />
              )}

              <CustomButton
                label={t('general.editor.save_changes')}
                variant="primary"
                onClick={handleSaveClip}
                disabled={saving || !hasChanged || isSceneProcessing || isRecording}
                loading={saving}
                loadingSpinner={true}
                className={styles.sidebarButton}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EditorSidebarActions;
